import posthog from 'posthog-js'

import type { AuthUser, PageProps } from '@/types'

type PosthogConfig = PageProps['posthog']

const privatePath = /\/(profile|registration|register|activity|admin|organizer)(\/|$)/i
let initialized = false
let identifiedId: string | null = null

export function initializePosthog(config: PosthogConfig): boolean {
  if (initialized) return true
  if (typeof window === 'undefined' || !config?.public_key) return false

  posthog.init(config.public_key, {
    api_host: config.host || 'https://us.i.posthog.com',
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    autocapture: true,
    disable_surveys: true,
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-private], [data-sensitive]',
    },
    disable_session_recording: !config.session_recording || privatePath.test(window.location.pathname),
    loaded: (client) => {
      if (config.environment) client.register({ environment: config.environment })
    },
  })
  initialized = true
  return true
}

export function syncPosthogUser(user?: AuthUser | null) {
  if (!initialized) return
  if (user) {
    const internalId = String(user.id)
    if (identifiedId !== internalId) {
      posthog.identify(internalId)
      identifiedId = internalId
    }
  } else if (identifiedId) {
    posthog.reset()
    identifiedId = null
  }
}

export function capturePageview(path: string, allowRecording: boolean) {
  if (!initialized) return
  const safePath = path.split('?')[0].split('#')[0]
  const shouldRecord = allowRecording && !privatePath.test(safePath)
  if (shouldRecord) posthog.startSessionRecording()
  else posthog.stopSessionRecording()
  posthog.capture('$pageview', { path: safePath })
}

export function captureEvent(name: string, properties: Record<string, string | number | boolean> = {}) {
  if (initialized) posthog.capture(name, properties)
}

export { posthog }
